import type { ExchangeObjItems } from '@skynet/data-exchanges';
import type { TradingApiKeysState } from './types';
import type { RequestStatus } from '@skynet/data-ws-core';
import { createSelector } from '@reduxjs/toolkit';
import { selectListExchange as selectScopedListExchange } from '@skynet/data-exchanges';

export interface Store {
  esTrading: {
    apiKeys: TradingApiKeysState;
  };
}

const EXCLUDED_EXCHANGE_IDS = [25, 28];

export const selectSelectedExchange = (store: Store) => store.esTrading.apiKeys.selectedExchange;
// There is no bespoke slice mirroring the ES exchange list. The `Trading` route
// and `TradingApiKeys` both subscribe via `useListPublicExchanges({ scope: 'es' })`
// to keep the cache populated. It reads `exchangesApi`, not the `Store` view above,
// so a unit-test fixture for it (or anything composed from it) needs both slices.
export const selectListExchange = selectScopedListExchange('es');
export const selectSupportedExchanges = createSelector(
  (state: Store) => state.esTrading.apiKeys.supportedExchanges,
  (supportedExchanges) => [...supportedExchanges].sort((a, b) => a - b),
);

export const selectFilteredExchanges = createSelector(
  [selectListExchange, selectSupportedExchanges],
  (exchanges, supportedIds) => {
    if (!exchanges || Object.keys(exchanges).length === 0 || !supportedIds.length) {
      return {};
    }

    const filtered = Object.entries(exchanges).reduce<ExchangeObjItems>((acc, [key, exchange]) => {
      const exchangeId = Number(key);

      if (supportedIds.includes(exchangeId)) {
        acc[key] = exchange;
      }
      return acc;
    }, {});

    return filtered;
  },
);

export const selectUnsupportedExchanges = createSelector(
  [selectListExchange, selectSupportedExchanges],
  (exchanges, supportedIds) => {
    if (!exchanges || Object.keys(exchanges).length === 0) {
      return {};
    }

    if (!supportedIds.length) {
      return {};
    }

    const filtered = Object.entries(exchanges).reduce<ExchangeObjItems>((acc, [key, exchange]) => {
      const exchangeId = Number(key);

      if (!supportedIds.includes(exchangeId) && !EXCLUDED_EXCHANGE_IDS.includes(exchangeId)) {
        acc[key] = exchange;
      }
      return acc;
    }, {});

    return filtered;
  },
);

export const selectSupportedExchangesStatus = (store: Store) =>
  store.esTrading.apiKeys.supportedExchangesStatus;

export const selectConnectedApiKeys = (store: Store) => {
  return store.esTrading.apiKeys.list;
};

export const selectConnectedAccountId = createSelector(
  [selectConnectedApiKeys, (_: Store, accountId?: string) => accountId],
  (apiKeys, accountId) => {
    if (!apiKeys?.length) {
      return null;
    }
    if (!accountId) {
      return null;
    }

    const hasAccount = apiKeys.some((item) => item.accountID.toString() === accountId);

    if (!hasAccount) {
      return null;
    }

    return accountId;
  },
);

export const selectDefaultConnectedApiKeys = createSelector(selectConnectedApiKeys, (apiKeys) => {
  if (!apiKeys?.length) {
    return null;
  }
  if (apiKeys[0].accountID) {
    return Number(apiKeys[0].accountID);
  }
  return null;
});

export const selectTargetAccount = (store: Store) => store.esTrading.apiKeys.targetAccount;

export const selectErrorByTargetApiKey = createSelector(
  selectTargetAccount,
  selectConnectedApiKeys,
  (targetAccount, apiList) => {
    if (targetAccount === null || !apiList.length) {
      return null;
    }
    return apiList.find((item) => item.accountID === +targetAccount)?.error ?? null;
  },
);

export const selectCurrentApiKey = createSelector(
  selectTargetAccount,
  selectConnectedApiKeys,
  selectDefaultConnectedApiKeys,
  (targetAccount, apiList, defaultApiKey) => {
    if (targetAccount === 'new' && apiList.length === 0) {
      return 'new';
    }
    if (targetAccount === null || !apiList.length) {
      return defaultApiKey ?? null;
    }
    const currentExchange = apiList.find((item) => item.accountID === +targetAccount);

    if (!currentExchange) {
      return defaultApiKey ?? null;
    }
    return currentExchange.accountID;
  },
);

export const selectSelectedExchangeByAccountId = createSelector(
  selectConnectedApiKeys,
  selectCurrentApiKey,
  (apiKeys, defaultApiKey) => {
    if (!apiKeys?.length) {
      return undefined;
    }
    if (defaultApiKey === null) {
      return undefined;
    }
    if (defaultApiKey === 'new') {
      return undefined;
    }
    return apiKeys.find((item) => item.accountID === defaultApiKey);
  },
);

export const selectTaskActionStatus = (state: Store) => state.esTrading.apiKeys.requestStatus;
export const selectApiKeySubmitted = (state: Store) =>
  state.esTrading.apiKeys.requestStatus === 'success';
export const selectResAddAPIKeyStatus = (state: Store) =>
  state.esTrading.apiKeys.resAddAPIKeyStatus;

export const selectUpdateApiKeyStatus = (state: Store) =>
  state.esTrading.apiKeys.resUpdateAPIKeyStatus;
export const selectDeleteApiKeyStatus = (state: Store) =>
  state.esTrading.apiKeys.resDeleteAPIKeyStatus;

/**
 * Status of the `Trader` command, the only source of the connected API keys.
 *
 * Exported so a page can tell "the investor list has not arrived yet" from
 * "it arrived and there is nothing in it": the second is a terminal state that
 * needs an explanation on screen, not a spinner (SKY-4581).
 */
export const selectInvestorStatus = (state: Store) => state.esTrading.apiKeys.isLoadingInvestor;

/**
 * A request status that will not change again without a new request.
 *
 * Exported so a page can tell "still waiting" from "answered, but with
 * nothing to show": rendering a spinner for the latter strands the user on a
 * placeholder that never resolves (SKY-4581).
 */
export const isSettled = (status: RequestStatus) => status === 'success' || status === 'error';

/**
 * True until the ES Dashboard bootstrap — the investor list (`Trader`) and the
 * account's supported exchanges — has answered at least once for the current
 * session. `null` (never requested) counts as loading, so the gap between
 * entering the section and the debounced init listen going out does not flash
 * an empty page first.
 *
 * This is a first-load flag, not an "in flight" flag: once both commands have
 * answered, later refreshes revalidate in the background and the status stays
 * `success` (see the `listen` case in the slice). `resetStore` — dispatched on
 * sign-out and tenant switch — puts it back to `null` so it re-arms per user.
 */
export const isLoadingEs = createSelector(
  selectInvestorStatus,
  selectSupportedExchangesStatus,
  (isLoadingInvestor, isLoadingSupportExchange) => {
    return !isSettled(isLoadingInvestor) || !isSettled(isLoadingSupportExchange);
  },
);
